import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { MONITORING_PREFERENCES } from '@app/shared';
import type { MonitoringAlertRowDto, MonitoringPreference } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import { api } from '../../lib/api';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const KEY = ['dashboard', 'monitoring', 'alert-settings'];

/**
 * "Monitoring Panel Email Setting" (switch `monitoring-alerts`): for each activity, which outcomes
 * are e-mailed (Notification Preferences) and to which addresses (Email, "+", Added Email).
 */
export function AlertSettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: KEY,
    queryFn: () =>
      api.get<{ data: MonitoringAlertRowDto[] }>('/dashboard/monitoring/alert-settings'),
    select: (r) => r.data,
    enabled: open,
  });
  const [rows, setRows] = useState<MonitoringAlertRowDto[]>([]);
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<Record<string, string>>({});
  useEffect(() => {
    if (open && settings.data) {
      setRows(settings.data);
      setTyped({});
      setProblem({});
    }
  }, [open, settings.data]);

  const save = useMutation({
    mutationFn: (next: MonitoringAlertRowDto[]) =>
      api.put<{ data: MonitoringAlertRowDto[] }>('/dashboard/monitoring/alert-settings', {
        rows: next,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });

  const change = (activity: string, patch: Partial<MonitoringAlertRowDto>) =>
    setRows((rs) => rs.map((r) => (r.activity === activity ? { ...r, ...patch } : r)));
  const add = (row: MonitoringAlertRowDto) => {
    const email = (typed[row.activity] ?? '').trim().toLowerCase();
    if (!EMAIL.test(email))
      return setProblem((p) => ({ ...p, [row.activity]: 'Enter a valid e-mail address' }));
    if (!row.emails.includes(email)) change(row.activity, { emails: [...row.emails, email] });
    setTyped((t) => ({ ...t, [row.activity]: '' }));
    setProblem((p) => ({ ...p, [row.activity]: '' }));
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync(rows);
      notify.success('Monitoring Panel Email Setting saved.');
      onClose();
    } catch (err) {
      notify.error(err);
    }
  };

  return (
    <FormDialog
      open={open}
      title="Monitoring Panel Email Setting"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      cancelLabel="Close"
      maxWidth="lg"
    >
      <Table size="small" aria-label="Monitoring Panel Email Setting">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Activity Name</TableCell>
            <TableCell>Notification Preferences</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Added Email</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row.activity}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{row.activity}</TableCell>
              <TableCell sx={{ minWidth: 260 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={row.preference}
                  onChange={(e) =>
                    change(row.activity, { preference: e.target.value as MonitoringPreference })
                  }
                  slotProps={{
                    htmlInput: { 'aria-label': `Notification Preferences of ${row.activity}` },
                  }}
                >
                  {MONITORING_PREFERENCES.map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell sx={{ minWidth: 240 }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                  <TextField
                    size="small"
                    type="email"
                    value={typed[row.activity] ?? ''}
                    onChange={(e) => setTyped((t) => ({ ...t, [row.activity]: e.target.value }))}
                    error={Boolean(problem[row.activity])}
                    helperText={problem[row.activity] || undefined}
                    slotProps={{
                      htmlInput: { 'aria-label': `Email for ${row.activity}`, maxLength: 120 },
                    }}
                  />
                  <IconButton aria-label={`Add email to ${row.activity}`} onClick={() => add(row)}>
                    <AddIcon />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {row.emails.map((email) => (
                    <Chip
                      key={email}
                      size="small"
                      label={email}
                      onDelete={() =>
                        change(row.activity, { emails: row.emails.filter((x) => x !== email) })
                      }
                    />
                  ))}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </FormDialog>
  );
}
